import { useState, useEffect } from "react";
import axios from "../axios";
import "../styles/register.css";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getGuestScans, clearGuestScans } from "../utils/guestStorage";
import { registerFcmToken } from "../utils/fcm";
import { trackAction } from "../services/sessionLogger";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const barcodeParam = new URLSearchParams(location.search).get("barcode");

  const [authMode, setAuthMode] = useState("email"); // "email" | "pseudo"
  const [emailSent, setEmailSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    role: barcodeParam ? "vendeur" : "client",
    website: "",       // honeypot
    form_loaded_at: Date.now(),
  });
  const [wantsPush, setWantsPush] = useState(false);

  // Pré-sélectionner le mode pseudo si ?mode=pseudo dans l'URL (ex: depuis WelcomeModal)
  useEffect(() => {
    const modeParam = new URLSearchParams(location.search).get("mode");
    if (modeParam === "pseudo") setAuthMode("pseudo");
  }, []);

  // Mise à jour de form_loaded_at au montage et à chaque changement de mode
  useEffect(() => {
  }, [authMode]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setFormData({
      name: "",
      email: "",
      username: "",
      password: "",
      role: barcodeParam ? "vendeur" : "client",
      website: "",
      form_loaded_at: Date.now(),
    });
  };

  // ── Modal phrase de récupération ──────────────────────────────────────────
  const [recoveryPhrase, setRecoveryPhrase] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCopyPhrase = () => {
    navigator.clipboard.writeText(recoveryPhrase).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRecoveryClose = () => {
    setRecoveryPhrase(null);
    const user = registeredUser;
    if (user?.role === "vendeur") {
      navigate(barcodeParam ? `/dashboard?barcode=${barcodeParam}` : "/dashboard");
    } else {
      navigate("/client");
    }
  };

  // ── Soumission ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    trackAction("S'inscrire");

    if (formData.password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (!/\d/.test(formData.password)) {
      toast.error("Le mot de passe doit contenir au moins 1 chiffre.");
      return;
    }

    try {
      let res;

      if (authMode === "email") {
        const payload = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          website: formData.website,
          form_loaded_at: formData.form_loaded_at,
        };
        if (wantsPush) payload.subscribedToPush = true;
        res = await axios.post(`/auth/register`, payload);
      } else {
        const payload = {
          username: formData.username,
          password: formData.password,
          role: formData.role,
          website: formData.website,
          form_loaded_at: formData.form_loaded_at,
        };
        if (wantsPush) payload.subscribedToPush = true;
        res = await axios.post(`/auth/register-pseudo`, payload);
      }

      const { user, token, message } = res.data;

      if (!token) {
        toast.error("⚠️ Aucun token reçu — vérifie la réponse du serveur.");
        return;
      }

      // 💾 Sauvegarde locale
      const userData = { ...user, token };
      localStorage.setItem("user", JSON.stringify(userData));

      // 🔔 Enregistrement FCM
      setTimeout(() => {
        registerFcmToken(user.id || user._id).catch((err) =>
          console.error("Erreur FCM :", err)
        );
      }, 1500);

      // 👤 Import des scans invités (clients uniquement)
      if (user.role === "client") {
        const guestScans = getGuestScans();
        if (guestScans.length > 0) {
          await axios.post(`/scans/batch`, {
            scans: guestScans.map((scan) => ({
              userId: user._id || user.id,
              id: scan.id,
              name: scan.name,
            })),
          });
          clearGuestScans();
          toast.success(`${guestScans.length} scan(s) importés avec succès`);
        }
      }

      toast.success(message || "🎉 Votre compte est créé ! Bienvenue sur CrushNow 💛");

      // Mode pseudo : afficher la modal phrase de récupération avant de naviguer
      if (authMode === "pseudo" && res.data.recovery_phrase) {
        setRegisteredUser(user);
        setRecoveryPhrase(res.data.recovery_phrase);
        return;
      }

      // Mode email : afficher écran de confirmation email si envoyé
      if (authMode === "email" && res.data.email_verification_sent === true) {
        setEmailSent(true);
        return;
      }

      // Mode email sans vérification : navigation directe après délai
      setTimeout(() => {
        if (user.role === "vendeur") {
          navigate(barcodeParam ? `/dashboard?barcode=${barcodeParam}` : "/dashboard");
        } else {
          navigate("/client");
        }
      }, 2500);

    } catch (error) {
      console.error("❌ Erreur inscription :", error);
      if (error.response?.status === 429) {
        toast.error("Trop de tentatives. Réessaie dans 15 minutes.");
        return;
      }
      toast.error(error.response?.data?.error || "Erreur à l'inscription");
    }
  };

  // ── Toggle mode ────────────────────────────────────────────────────────────
  const toggleStyle = (active) => ({
    flex: 1,
    padding: "8px",
    border: "1.5px solid",
    borderColor: active ? "#D41A6E" : "#CBD5E1",
    borderRadius: "8px",
    background: active ? "#D41A6E" : "white",
    color: active ? "white" : "#64748b",
    fontWeight: active ? 700 : 400,
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.15s",
  });

  return (
    <div className="register-wrapper">
      <ToastContainer theme="colored" />

      {emailSent && (
        <div style={{
          textAlign: "center", padding: "32px 24px",
          background: "#f0fdf4", border: "1px solid #86efac",
          borderRadius: "12px", margin: "16px"
        }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📧</div>
          <h2 style={{ color: "#166534", marginBottom: "8px" }}>Vérifiez votre email !</h2>
          <p style={{ color: "#475569", fontSize: "14px", marginBottom: "16px" }}>
            Un lien de confirmation a été envoyé à votre adresse.<br />
            Cliquez dessus pour activer votre compte.
          </p>
          <p style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "16px" }}>
            Le lien expire dans 24 heures.
          </p>
          <button
            onClick={async () => {
              await axios.post("/auth/resend-verification", { email: formData.email });
              toast.success("📧 Email renvoyé !");
            }}
            className="register-btn"
            style={{ background: "#f1f5f9", color: "#475569" }}
          >
            Renvoyer l'email
          </button>
          <div style={{ marginTop: "12px" }}>
            <Link to="/login" className="register-link">
              Déjà confirmé ? Se connecter
            </Link>
          </div>
        </div>
      )}

      {/* ── Modal phrase de récupération ── */}
      {recoveryPhrase && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: "24px"
        }}>
          <div style={{
            maxWidth: "480px", width: "100%", background: "#1e293b",
            borderRadius: "14px", padding: "32px", color: "white"
          }}>
            <p style={{ color: "#fbbf24", fontWeight: 700, fontSize: "15px", marginBottom: "20px", lineHeight: 1.5 }}>
              ⚠️ Notez ces 12 mots. Ils sont la seule façon de récupérer votre compte. Ils ne seront plus jamais affichés.
            </p>
            <p style={{
              fontSize: "18px", fontWeight: 700, lineHeight: 2,
              color: "white", marginBottom: "28px",
              background: "#0f172a", borderRadius: "8px", padding: "16px",
              wordBreak: "break-word", letterSpacing: "0.02em"
            }}>
              {recoveryPhrase}
            </p>
            <button
              onClick={handleCopyPhrase}
              style={{
                width: "100%", padding: "12px", marginBottom: "10px",
                background: copied ? "#0f172a" : "#1e40af",
                border: "2px solid " + (copied ? "#16a34a" : "#3b82f6"),
                borderRadius: "8px", color: "white",
                fontWeight: 700, fontSize: "15px", cursor: "pointer",
                transition: "background 0.2s"
              }}
            >
              {copied ? "✅ Copié !" : "📋 Copier mes 12 mots"}
            </button>
            <button
              onClick={handleRecoveryClose}
              style={{
                width: "100%", padding: "14px", background: "#16a34a",
                border: "none", borderRadius: "8px", color: "white",
                fontWeight: 700, fontSize: "16px", cursor: "pointer"
              }}
            >
              J'ai noté mes mots
            </button>
          </div>
        </div>
      )}

      {!emailSent && <form onSubmit={handleSubmit} className="register-card">
        <h2 className="register-title">Inscription</h2>

        {/* ── Toggle mode ── */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
          <button type="button" style={toggleStyle(authMode === "email")} onClick={() => switchMode("email")}>
            Avec email
          </button>
          <button type="button" style={toggleStyle(authMode === "pseudo")} onClick={() => switchMode("pseudo")}>
            Sans email
          </button>
        </div>

        {/* Honeypot (caché) */}
        <input
          type="text"
          name="website"
          value={formData.website || ""}
          onChange={handleChange}
          style={{ display: "none" }}
          tabIndex="-1"
          autoComplete="off"
        />

        {/* ── Mode email ── */}
        {authMode === "email" && (
          <>
            <input
              name="name"
              placeholder="Nom"
              onChange={handleChange}
              onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
              className="register-input"
              required
            />
            <input
              name="email"
              type="email"
              placeholder="Email"
              onChange={handleChange}
              onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
              className="register-input"
              required
            />
          </>
        )}

        {/* ── Mode pseudo ── */}
        {authMode === "pseudo" && (
          <input
            name="username"
            placeholder="Pseudonyme"
            onChange={handleChange}
            onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
            className="register-input"
            required
          />
        )}

        <div style={{ position: "relative" }}>
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Mot de passe"
            onChange={handleChange}
            onFocus={(e) => { setTimeout(() => { e.target.scrollIntoView({ behavior: "smooth", block: "center" }); }, 300); }}
            className="register-input"
            required
            style={{ paddingRight: "40px", width: "100%" }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: "absolute", right: "10px", top: "50%",
              transform: "translateY(-50%)", background: "none",
              border: "none", cursor: "pointer", color: "#94a3b8",
              fontSize: "18px", padding: "0"
            }}
          >
            {showPassword ? "🙈" : "👁️"}
          </button>
        </div>
        <p style={{ fontSize: "11px", color: "#94a3b8", marginTop: "4px" }}>
          Minimum 8 caractères dont 1 chiffre
        </p>

        <select
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="register-input"
        >
          <option value="client">Client</option>
          <option value="vendeur">Vendeur</option>
        </select>

        <label className="register-checkbox-label">
          <input
            type="checkbox"
            checked={wantsPush}
            onChange={(e) => setWantsPush(e.target.checked)}
          />
          🔔 Activer les notifications
        </label>

        <div style={{
          background: "#f0fdf4",
          border: "1px solid #86efac",
          borderRadius: "8px",
          padding: "10px 14px",
          marginBottom: "14px",
          fontSize: "12px",
          color: "#166534",
          lineHeight: "1.6"
        }}>
          🔒 <strong>Pas de pub. Pas de revente de données.</strong><br />
          Les magasins voient uniquement des stats anonymes — jamais ton nom.<br />
          Tu peux tout supprimer à tout moment.
        </div>

        <label style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: "8px",
          fontSize: "12px",
          color: "#475569",
          marginBottom: "12px",
          cursor: "pointer",
          lineHeight: "1.5",
          width: "100%",
          boxSizing: "border-box",
          paddingRight: "8px",
          flexWrap: "nowrap"
        }}>
          <input
            type="checkbox"
            required
            style={{ marginTop: "3px", flexShrink: 0, width: "16px", height: "16px", accentColor: "#e11d48" }}
          />
          J'ai au moins 13 ans et j'accepte les{" "}
          <a href="/mentions" style={{ color: "#e11d48" }} target="_blank" rel="noopener noreferrer">
            CGU
          </a>{" "}
          et la{" "}
          <a href="/privacy" style={{ color: "#e11d48" }} target="_blank" rel="noopener noreferrer">
            Politique de confidentialité
          </a>
        </label>

        <button type="submit" className="register-btn">
          S'inscrire
        </button>

        <div className="register-links">
          <p>
            Déjà inscrit ?{" "}
            <Link to="/login" className="register-link">
              Connexion
            </Link>
          </p>
          <Link to="/" className="register-link">
            Retour à l'accueil
          </Link>
        </div>
      </form>}
    </div>
  );
};

export default RegisterPage;
